import type { AxiosInstance, AxiosRequestConfig } from "axios";
import {
  type OperationResponse,
  type Parameters,
  type UnknownParamsObject,
} from "../../common.ts";
import { omitPathParameters, replacePathParameters } from "../../utils.ts";
import type * as Paths from "./paths";

interface OperationMethods {
  "getBankings"(
    parameters?: Parameters<Paths.GetBankings.QueryParameters> | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.GetBankings.Responses.$200>;

  "createBanking"(
    parameters?: Parameters<UnknownParamsObject> | null,
    data?: Paths.CreateBanking.RequestBody,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.CreateBanking.Responses.$200>;

  "getBanking"(
    parameters?: Parameters<
      Paths.GetBanking.QueryParameters & Paths.GetBanking.PathParameters
    > | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.GetBanking.Responses.$200>;

  "updateBanking"(
    parameters?: Parameters<Paths.UpdateBanking.PathParameters> | null,
    data?: Paths.UpdateBanking.RequestBody,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.UpdateBanking.Responses.$200>;

  "deleteBanking"(
    parameters?: Parameters<Paths.DeleteBanking.PathParameters> | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.DeleteBanking.Responses.$204>;
}

export const getBankingOperations = (client: AxiosInstance) => {
  const getBankings: OperationMethods["getBankings"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/banking/banking";
    return client.get(replacePathParameters(url, parameters), {
      params: omitPathParameters(parameters, url),
      data: data,
      ...config,
    });
  };
  const createBanking: OperationMethods["createBanking"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/banking/banking";
    return client.post(replacePathParameters(url, parameters), data, {
      params: omitPathParameters(parameters, url),
      ...config,
    });
  };
  const getBanking: OperationMethods["getBanking"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/banking/banking/{banking}";
    return client.get(replacePathParameters(url, parameters), {
      params: omitPathParameters(parameters, url),
      data: data,
      ...config,
    });
  };
  const updateBanking: OperationMethods["updateBanking"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/banking/banking/{banking}";
    return client.put(replacePathParameters(url, parameters), data, {
      params: omitPathParameters(parameters, url),
      ...config,
    });
  };
  const deleteBanking: OperationMethods["deleteBanking"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/banking/banking/{banking}";
    return client.delete(replacePathParameters(url, parameters), {
      params: omitPathParameters(parameters, url),
      data: data,
      ...config,
    });
  };
  return {
    getBankings,
    createBanking,
    getBanking,
    updateBanking,
    deleteBanking,
  };
};
